import React, { useState } from "react";
import { Field, reduxForm, getFormSyncErrors, FormSection } from "redux-form";
import { ACCOUNT_TYPES as accountTypes } from "@constants/achActionTypes";
import { trimString } from "@utils";
import { addACH } from "@actions/paymentMethods";
import styled from "@emotion/styled";
import loadable from "@loadable/component";
const MessageContainer = loadable(() =>
  import(/* webpackChunkName: "MessageContainer" */ "./MessageContainer")
);
import RenderFormFieldNew from "@components/forms/RenderFormFieldNew";
import RenderDropdownNew from "@components/forms/RenderDropdownNew";
import {
  accountNumber,
  routingNumber,
  accountType,
  validateAccountAndReEnterNumber
} from "@components/validation";
import CustomSubmitButton from "./CustomSubmitButton";
import { defaultAction } from "@actions/defaultAction";
import { SET_OPEN_ADD_BANK_ACCT_MODAL } from "@constants/achActionTypes";
import SmallButtonSpinner from "@components/SmallButtonSpinner";
import { useDispatch, useSelector } from "react-redux";

const FieldContainer = styled.div`
  margin-bottom: 17px;
`;

const ButtonContainer = styled.div`
  margin-top: 45px;
`;

const Form = styled.form`
  margin-top: 20px;
`;

const AddBankAccountForm = ({ handleSubmit, invalid, submitFailed }) => {
  const dispatch = useDispatch();
  const addBankAccountErrors = useSelector(state =>
    getFormSyncErrors("addBankAccount")(state)
  );

  const [errorFromServer, setErrorFromServer] = useState(null);
  const [addText, setAddText] = useState("Submit");
  const [addButton, setAddButton] = useState("ORANGE_BUTTON");
  const [loading, setLoading] = useState(false);

  const submit = async fields => {
    if (loading || invalid) return;
    setLoading(true);
    const response = await dispatch(
      addACH({
        routingNumber: trimString(fields.enterACH.routingNumber),
        accountNumber: trimString(fields.enterACH.accountNumber),
        accountType: trimString(fields.enterACH.accountType.toLowerCase()),
        default: false
      })
    );

    setLoading(false);
    if (response === "success") {
      setAddText("Success");
      setAddButton("GREEN_BUTTON");
      setTimeout(() => {
        dispatch(defaultAction(SET_OPEN_ADD_BANK_ACCT_MODAL, false));
      }, 1000);
    } else if (response?.error) {
      setErrorFromServer(response.error);
    }
  };
  return (
    <Form onSubmit={handleSubmit(submit)} className={"add-bank-account-form"}>
      {((submitFailed &&
        addBankAccountErrors &&
        Object.keys(addBankAccountErrors).length) ||
        errorFromServer) && (
        <div>
          <MessageContainer
            message={
              errorFromServer || "Please fill out all the fields in the form."
            }
            classes={"credit-card-form-error-message"}
          />
        </div>
      )}
      <FormSection name="enterACH">
        <FieldContainer>
          <Field
            name="accountNumber"
            label="accountNumber"
            component={RenderFormFieldNew}
            type="text"
            placeholder="Account Number"
            validate={accountNumber}
            paddingLeft={"30px"}
            addIcon={
              "https://cdn.cardcash.com/website/ui/elements/basic%20icons%20info.svg"
            }
            hoverImage={
              "https://cdn.cardcash.com/website/ui/elements/ach_account.png"
            }
            imageContainerStyles={{
              right: "16px",
              width: "36px",
              display: "flex",
              justifyContent: "center",
              height: "100%",
              alignItems: "center"
            }}
          />
        </FieldContainer>
        <FieldContainer>
          <Field
            name="accountNumberConfirm"
            label="accountNumberConfirm"
            component={RenderFormFieldNew}
            type="text"
            placeholder="Re-enter Account Number"
            validate={validateAccountAndReEnterNumber}
            paddingLeft={"30px"}
            addIcon={
              "https://cdn.cardcash.com/website/ui/elements/basic%20icons%20info.svg"
            }
            imageContainerStyles={{
              right: "16px",
              width: "36px",
              display: "flex",
              justifyContent: "center",
              height: "100%",
              alignItems: "center"
            }}
          />
        </FieldContainer>
        <FieldContainer>
          <Field
            name="routingNumber"
            label="routingNumber"
            component={RenderFormFieldNew}
            onChangeAction={() => {
              setErrorFromServer(null);
            }}
            type="text"
            placeholder="Routing Number"
            paddingLeft={"30px"}
            validate={routingNumber}
            addIcon={
              "https://cdn.cardcash.com/website/ui/elements/basic%20icons%20info.svg"
            }
            hoverImage={
              "https://cdn.cardcash.com/website/ui/elements/ach_routing.png"
            }
            imageContainerStyles={{
              right: "16px",
              width: "36px",
              display: "flex",
              justifyContent: "center",
              height: "100%",
              alignItems: "center"
            }}
          />
        </FieldContainer>
        <FieldContainer>
          <Field
            name="accountType"
            label="accountType"
            component={RenderDropdownNew}
            type="text"
            placeholder="Account Type"
            list={accountTypes}
            validate={accountType}
            defaultValue={"Account Type"}
            addArrow={true}
            className={"add-bank-account-account-type"}
          />
        </FieldContainer>
      </FormSection>
      <ButtonContainer>
        <CustomSubmitButton
          type="submit"
          styles={{ height: "72px", borderRadius: "36px" }}
          buttonType={addButton}
          appearDisabled={invalid}
        >
          {loading ? (
            <SmallButtonSpinner img="https://cdn.cardcash.com/website/ui/elements/spinner-white.png" />
          ) : (
            <span>{addText}</span>
          )}
        </CustomSubmitButton>
      </ButtonContainer>
    </Form>
  );
};

export default reduxForm({
  form: "addBankAccount",
  enableReinitialize: true
})(AddBankAccountForm);
